import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomerOrganization, Identity, Organization } from '@b3networks/api/auth';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PurchaseAddonModel } from '../purchase/purchase-addon.model';
import { PurchaseModel } from '../purchase/purchase.model';
import { User } from './customer.model';
import { MemberContactsStore } from './customer.store';

@Injectable({ providedIn: 'root' })
export class CustomerService {
  constructor(
    private store: MemberContactsStore,
    private http: HttpClient
  ) {}

  getNewOrgs(pageNumber: number, pageSize: number): Observable<Organization[]> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('needWallet', String(true));

    return this.http.get<Organization[]>('/customer/private/v1/organizations/new', { params });
  }

  getPartners(keyword?: string): Observable<Organization[]> {
    let params = new HttpParams();
    if (keyword) {
      params = params.append('keyword', keyword);
    }

    return this.http
      .get<Organization[]>('/customer/private/v2/partners', { params })
      .pipe(map(data => data.reverse().map(x => new Organization(x))));
  }

  updateFavorite(orgUuid: string, favorite: boolean) {
    return this.http.put(`/customer/private/v1/organizations/${orgUuid}/favorite`, { favorite });
  }

  findIdentities(code: string, keyword: string, pageNumber?: number, pageSize?: number): Observable<Identity[]> {
    const params = new HttpParams()
      .set('keyword', keyword)
      .set('page', pageNumber)
      .set('pageSize', pageSize)
      .set('needWallet', String(true));

    return this.http
      .get<Identity[]>(`/customer/private/v1/search/identities/${code}`, { params })
      .pipe(map(list => list.map(iDentity => new Identity(iDentity))));
  }

  findOrg(code: string, keyword: string, pageNumber?: number, pageSize?: number): Observable<Organization[]> {
    let params = new HttpParams().set('keyword', keyword).set('needWallet', String(true));
    if (pageNumber || pageNumber === 0) {
      params = params.set('page', pageNumber);
    }
    if (pageSize) {
      params = params.set('pageSize', pageSize);
    }
    return this.http
      .get<Organization[]>(`/customer/private/v1/search/organizations/${code}`, { params })
      .pipe(map(list => list.map(org => new Organization(org))));
  }

  findOrgsByIdentity(identityUuid: string): Observable<Organization[]> {
    const params = new HttpParams().set('identityUuid', identityUuid);
    return this.http.get<Organization[]>(`/customer/private/v1/organizations`, { params });
  }

  isEmailExisted(email: string): Observable<User> {
    const params = new HttpParams().set('email', email);
    return this.http.get<User>(`/customer/private/v1/identities/email`, { params });
  }

  exportBillingEmails(email: string, orgUuids?: string[]) {
    if (!orgUuids || orgUuids.length === 0) {
      const params = new HttpParams().set('email', email);
      return this.http.get(`/customer/private/v1/export/billing-emails`, { params });
    }

    const body = { orgUuids, email };
    return this.http.post(`/customer/private/v1/export/billing-emails`, body);
  }

  exportOwnersEmail(email: string, orgUuids?: string[]) {
    if (!orgUuids || orgUuids.length === 0) {
      const params = new HttpParams().set('email', email);
      return this.http.get(`/customer/private/v1/export/owner-emails`, { params });
    }

    const body = { orgUuids, email };
    return this.http.post(`/customer/private/v1/export/owner-emails`, body);
  }

  getPlans(productId: string, buyerUuid: string, contractNumber?: string): Observable<PurchaseModel> {
    let params = new HttpParams().set('buyerUuid', buyerUuid);
    if (contractNumber) {
      params = params.set('contractNumber', contractNumber);
    }

    return this.http.get<PurchaseModel>(`/license/private/v1/apps/${productId}`, { params }).pipe(
      tap(x => x.plans.forEach(p => (p.selectedSaleModel = p.saleModels[0]))),
      map(x => new PurchaseModel(x))
    );
  }

  getAddons(
    productId: string,
    planUuid: string,
    saleModelCode: string,
    buyerUuid: string,
    contractNumber: string
  ): Observable<PurchaseAddonModel> {
    let params = new HttpParams().set('buyerUuid', buyerUuid).set('saleModelCode', saleModelCode);
    if (contractNumber) {
      params = params.set('contractNumber', contractNumber);
    }

    return this.http.get<PurchaseAddonModel>(`/license/private/v1/apps/${productId}/plans/${planUuid}`, {
      params
    });
  }

  searchExternalCustomer(key: string, value: string): Observable<CustomerOrganization[]> {
    const params = new HttpParams().set(key, value);
    return this.http.get<CustomerOrganization[]>(`billing/private/v1/customer`, { params }).pipe(
      map(items => {
        return items.map(item => ({
          entityId: item.entityId,
          uuid: item.customerId,
          name: item.name
        }));
      })
    );
  }

  searchReferenceCustomer(key: string, value: string): Observable<string[]> {
    const params = new HttpParams().set(key, value);
    return this.http.get<string[]>(`billing/private/v6/buyerTagKey/CustomerRef`, { params });
  }
}
